import React, { useState, useEffect } from 'react';
import type { User } from '@supabase/supabase-js';
import { supabase } from './supabaseClient';
import { ROLE_LABELS } from './CompanyContext';
import { acceptInvitation, rememberInviteToken, forgetInviteToken } from './lib/invitaciones';

interface Invitation {
  id: string;
  email: string;
  company_id: string;
  company_name: string;
  role: string;
  accepted_at: string | null;
  created_at: string;
  // Desde 20260930030000 (con respaldo a created_at + 7 días si no llegan)
  expires_at?: string | null;
  status?: string | null;
}

interface Props {
  token: string;
  // Sesión abierta al llegar al enlace (o la que se abre al aceptar). El
  // enlace funciona con o sin sesión: App.tsx ya no oculta JoinPage si hay usuario.
  user: User | null;
}

type PageState = 'loading' | 'error' | 'form-new' | 'form-existing' | 'form-logged' | 'wrong-account' | 'check-email' | 'success';

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

// Misma regla que accept_invitation: expires_at manda; si falta, created_at + 7 días.
function isExpired(inv: Invitation): boolean {
  const vence = inv.expires_at
    ? new Date(inv.expires_at).getTime()
    : new Date(inv.created_at).getTime() + SEVEN_DAYS_MS;
  return Date.now() >= vence;
}

// Pendiente pero con otro estado puesto a mano (p. ej. 'revoked').
function isRevoked(inv: Invitation): boolean {
  return !inv.accepted_at && !!inv.status && inv.status !== 'pending' && inv.status !== 'accepted';
}

const sameEmail = (a?: string | null, b?: string | null) =>
  !!a && !!b && a.trim().toLowerCase() === b.trim().toLowerCase();

export default function JoinPage({ token, user }: Props) {
  const [pageState, setPageState]     = useState<PageState>('loading');
  const [invitation, setInvitation]   = useState<Invitation | null>(null);
  const [errorMsg, setErrorMsg]       = useState('');
  const [fullName, setFullName]       = useState('');
  const [password, setPassword]       = useState('');
  const [confirmPwd, setConfirmPwd]   = useState('');
  const [showPwd, setShowPwd]         = useState(false);
  const [submitting, setSubmitting]   = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Acepta en el servidor (rol y empresa salen de la invitación) y vuelve al
  // inicio sin el token en la URL. Idempotente: si ya se aceptó, también redirige.
  async function finish(): Promise<void> {
    const res = await acceptInvitation(token);
    if (!res.ok) {
      setSubmitError(res.message);
      setSubmitting(false);
      return;
    }
    setPageState('success');
    setSubmitting(false);
    setTimeout(() => { window.location.replace('/'); }, 1500);
  }

  useEffect(() => {
    async function loadInvitation() {
      const { data, error } = await supabase.rpc('get_invitation_by_token', { p_token: token });

      if (error || !data || (data as Invitation[]).length === 0) {
        setErrorMsg('Esta invitación no existe o ya fue utilizada.');
        setPageState('error');
        return;
      }

      const inv = (data as Invitation[])[0];
      setInvitation(inv);

      // Con sesión abierta: si es la cuenta invitada, se acepta desde aquí;
      // si es otra cuenta, se ofrece cerrar sesión.
      if (user) {
        if (!sameEmail(user.email, inv.email)) {
          setPageState('wrong-account');
          return;
        }
        if (inv.accepted_at) {
          // Ya aceptada (p. ej. por App.tsx al confirmar el correo): la RPC
          // responde 'ya_aceptada' si es este mismo usuario y redirige.
          setSubmitting(true);
          await finish();
          setPageState((prev) => (prev === 'success' ? prev : 'form-logged'));
          return;
        }
        if (isRevoked(inv)) {
          setErrorMsg('Esta invitación ya no está vigente. Solicita una nueva al administrador.');
          setPageState('error');
          return;
        }
        if (isExpired(inv)) {
          setErrorMsg('Esta invitación ha expirado. Solicita una nueva al administrador.');
          setPageState('error');
          return;
        }
        setPageState('form-logged');
        return;
      }

      if (inv.accepted_at) {
        setErrorMsg('Esta invitación ya fue aceptada. Inicia sesión normalmente en StorSafe.');
        setPageState('error');
        return;
      }

      if (isRevoked(inv)) {
        setErrorMsg('Esta invitación ya no está vigente. Solicita una nueva al administrador.');
        setPageState('error');
        return;
      }

      if (isExpired(inv)) {
        setErrorMsg('Esta invitación ha expirado. Solicita una nueva al administrador.');
        setPageState('error');
        return;
      }

      // ¿El correo de la invitación ya tiene cuenta? Lo responde validate-corporate-email
      // con service_role a partir del token (límite 'registro'); check_email_exists ya no es público.
      const { data: chk } = await supabase.functions.invoke('validate-corporate-email', { body: { invitation_token: token } });
      const emailExists = chk?.email_registrado === true;

      if (emailExists) {
        setPageState('form-existing');
      } else {
        setPageState('form-new');
      }
    }

    loadInvitation();
    // Solo al abrir el enlace: iniciar sesión dentro de esta página no debe
    // volver a cargar la invitación (finish() ya se encarga).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // ── Aceptar con la sesión ya abierta ───────────────────────────────────────
  async function handleLoggedAccept() {
    setSubmitError('');
    setSubmitting(true);
    await finish();
  }

  // ── Otra cuenta con sesión abierta: cerrar sesión y volver al enlace ───────
  async function handleSwitchAccount() {
    setSubmitting(true);
    await supabase.auth.signOut();
    window.location.reload();
  }

  // ── Aceptar invitación para usuario existente ──────────────────────────────
  async function handleExistingUser(e: React.FormEvent) {
    e.preventDefault();
    setSubmitError('');
    setSubmitting(true);

    const inv = invitation!;

    const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
      email: inv.email,
      password,
    });

    if (signInError || !signInData.user) {
      setSubmitError('Contraseña incorrecta. Intenta de nuevo.');
      setSubmitting(false);
      return;
    }

    await finish();
  }

  // ── Crear cuenta nueva ─────────────────────────────────────────────────────
  async function handleNewUser(e: React.FormEvent) {
    e.preventDefault();
    if (password !== confirmPwd) {
      setSubmitError('Las contraseñas no coinciden.');
      return;
    }
    setSubmitError('');
    setSubmitting(true);

    const inv = invitation!;

    // Si Supabase pide confirmar el correo, el enlace de confirmación vuelve a
    // /join?token=… y, como respaldo (otra pestaña, otra URL), App.tsx toma el
    // token de localStorage al abrirse la sesión.
    rememberInviteToken(token);

    const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
      email: inv.email,
      password,
      options: {
        data: { full_name: fullName.trim() },
        emailRedirectTo: `${window.location.origin}/join?token=${encodeURIComponent(token)}`,
      },
    });

    if (signUpError) {
      forgetInviteToken();
      setSubmitError(translateError(signUpError.message));
      setSubmitting(false);
      return;
    }

    if (signUpData.session) {
      await finish();
    } else {
      setPageState('check-email');
      setSubmitting(false);
    }
  }

  // ── Loading ────────────────────────────────────────────────────────────────
  if (pageState === 'loading') {
    return (
      <div className="login-root">
        <div className="login-card" style={{ textAlign: 'center' }}>
          <div className="login-logo" style={{ justifyContent: 'center' }}>
            <div className="logo-icon"><StorSafeLogo /></div>
            <h1 className="logo-name">StorSafe</h1>
          </div>
          <p style={{ color: '#94a3b8', marginTop: 32 }}>Verificando invitación…</p>
        </div>
      </div>
    );
  }

  // ── Error ──────────────────────────────────────────────────────────────────
  if (pageState === 'error') {
    return (
      <div className="login-root">
        <div className="login-card">
          <div className="login-logo">
            <div className="logo-icon"><StorSafeLogo /></div>
            <h1 className="logo-name">StorSafe</h1>
          </div>
          <p className="login-error" style={{ marginTop: 28 }}>{errorMsg}</p>
          <p style={{ textAlign: 'center', marginTop: 20, fontSize: '0.85rem' }}>
            <a href="/" style={{ color: '#22c55e', textDecoration: 'none' }}>
              ← Volver al inicio de sesión
            </a>
          </p>
          <p className="login-footer">© 2026 StorSafe · Todos los derechos reservados</p>
        </div>
      </div>
    );
  }

  // ── Success ────────────────────────────────────────────────────────────────
  if (pageState === 'success') {
    return (
      <div className="login-root">
        <div className="login-card" style={{ textAlign: 'center' }}>
          <div className="login-logo" style={{ justifyContent: 'center' }}>
            <div className="logo-icon"><StorSafeLogo /></div>
            <h1 className="logo-name">StorSafe</h1>
          </div>
          <div style={{
            marginTop: 28,
            padding: '24px 20px',
            background: 'rgba(34,197,94,0.07)',
            borderRadius: 12,
            border: '1px solid rgba(34,197,94,0.2)',
          }}>
            <p style={{ fontSize: '2.2rem', lineHeight: 1 }}>✅</p>
            <p style={{ color: '#e2e8f0', fontWeight: 600, marginTop: 14, fontSize: '1rem' }}>
              ¡Invitación aceptada!
            </p>
            <p style={{ color: '#94a3b8', fontSize: '0.84rem', marginTop: 10, lineHeight: 1.65 }}>
              Tu cuenta ha sido vinculada a <strong style={{ color: '#e2e8f0' }}>{invitation?.company_name}</strong>.
              Redirigiendo al dashboard…
            </p>
          </div>
          <p className="login-footer">© 2026 StorSafe · Todos los derechos reservados</p>
        </div>
      </div>
    );
  }

  // ── Check email ────────────────────────────────────────────────────────────
  if (pageState === 'check-email') {
    return (
      <div className="login-root">
        <div className="login-card" style={{ textAlign: 'center' }}>
          <div className="login-logo" style={{ justifyContent: 'center' }}>
            <div className="logo-icon"><StorSafeLogo /></div>
            <h1 className="logo-name">StorSafe</h1>
          </div>
          <div style={{
            marginTop: 28,
            padding: '24px 20px',
            background: 'rgba(34,197,94,0.07)',
            borderRadius: 12,
            border: '1px solid rgba(34,197,94,0.2)',
          }}>
            <p style={{ fontSize: '2.2rem', lineHeight: 1 }}>📧</p>
            <p style={{ color: '#e2e8f0', fontWeight: 600, marginTop: 14, fontSize: '1rem' }}>
              Revisa tu correo
            </p>
            <p style={{ color: '#94a3b8', fontSize: '0.84rem', marginTop: 10, lineHeight: 1.65 }}>
              Enviamos un enlace de confirmación a{' '}
              <strong style={{ color: '#e2e8f0' }}>{invitation?.email}</strong>.
              Haz clic en él para activar tu cuenta y serás redirigido automáticamente al dashboard.
            </p>
          </div>
          <p className="login-footer">© 2026 StorSafe · Todos los derechos reservados</p>
        </div>
      </div>
    );
  }

  // ── Sesión abierta con la cuenta invitada ──────────────────────────────────
  if (pageState === 'form-logged') {
    return (
      <div className="login-root">
        <div className="login-card">
          <div className="login-logo">
            <div className="logo-icon"><StorSafeLogo /></div>
            <h1 className="logo-name">StorSafe</h1>
          </div>

          <p className="login-tagline" style={{ textTransform: 'none', letterSpacing: 0 }}>
            Has sido invitado a unirte a{' '}
            <strong style={{ color: '#e2e8f0' }}>{invitation?.company_name}</strong>
          </p>

          <div style={{
            margin: '16px 0',
            padding: '12px 16px',
            background: 'rgba(34,197,94,0.07)',
            borderRadius: 10,
            border: '1px solid rgba(34,197,94,0.2)',
            fontSize: '0.84rem',
            color: '#94a3b8',
            lineHeight: 1.6,
          }}>
            Iniciaste sesión como <strong style={{ color: '#e2e8f0' }}>{user?.email}</strong>.
            Al aceptar, te unirás con el rol{' '}
            <strong style={{ color: '#e2e8f0' }}>{ROLE_LABELS[invitation?.role ?? ''] ?? invitation?.role}</strong>.
          </div>

          {submitError && <p className="login-error">{submitError}</p>}

          <button
            type="button"
            className="btn-login"
            onClick={handleLoggedAccept}
            disabled={submitting}
          >
            {submitting ? <span className="btn-spinner" /> : 'Aceptar invitación'}
          </button>

          <p style={{ textAlign: 'center', marginTop: 16, fontSize: '0.82rem', color: '#64748b' }}>
            ¿No eres tú?{' '}
            <button
              type="button"
              onClick={handleSwitchAccount}
              disabled={submitting}
              style={{ background: 'none', border: 'none', padding: 0, color: '#22c55e', cursor: 'pointer', fontSize: 'inherit' }}
            >
              Cerrar sesión
            </button>
          </p>

          <p className="login-footer">© 2026 StorSafe · Todos los derechos reservados</p>
        </div>
      </div>
    );
  }

  // ── Sesión abierta con OTRA cuenta ─────────────────────────────────────────
  if (pageState === 'wrong-account') {
    return (
      <div className="login-root">
        <div className="login-card">
          <div className="login-logo">
            <div className="logo-icon"><StorSafeLogo /></div>
            <h1 className="logo-name">StorSafe</h1>
          </div>

          <div style={{
            marginTop: 28,
            padding: '12px 16px',
            background: 'rgba(249,115,22,0.08)',
            borderRadius: 10,
            border: '1px solid rgba(249,115,22,0.25)',
            fontSize: '0.84rem',
            color: '#94a3b8',
            lineHeight: 1.6,
          }}>
            Esta invitación a <strong style={{ color: '#e2e8f0' }}>{invitation?.company_name}</strong> es para{' '}
            <strong style={{ color: '#e2e8f0' }}>{invitation?.email}</strong>, pero tienes abierta la sesión de{' '}
            <strong style={{ color: '#e2e8f0' }}>{user?.email}</strong>.
            Cierra sesión para continuar con la cuenta invitada.
          </div>

          <button
            type="button"
            className="btn-login"
            style={{ marginTop: 20 }}
            onClick={handleSwitchAccount}
            disabled={submitting}
          >
            {submitting ? <span className="btn-spinner" /> : 'Cerrar sesión y continuar'}
          </button>

          <p style={{ textAlign: 'center', marginTop: 16, fontSize: '0.82rem' }}>
            <a href="/" style={{ color: '#22c55e', textDecoration: 'none' }}>
              Volver a StorSafe sin aceptar
            </a>
          </p>

          <p className="login-footer">© 2026 StorSafe · Todos los derechos reservados</p>
        </div>
      </div>
    );
  }

  // ── Form — usuario existente ───────────────────────────────────────────────
  if (pageState === 'form-existing') {
    return (
      <div className="login-root">
        <div className="login-card">
          <div className="login-logo">
            <div className="logo-icon"><StorSafeLogo /></div>
            <h1 className="logo-name">StorSafe</h1>
          </div>

          <p className="login-tagline" style={{ textTransform: 'none', letterSpacing: 0 }}>
            Has sido invitado a unirte a{' '}
            <strong style={{ color: '#e2e8f0' }}>{invitation?.company_name}</strong>
          </p>

          <div style={{
            margin: '16px 0',
            padding: '12px 16px',
            background: 'rgba(249,115,22,0.08)',
            borderRadius: 10,
            border: '1px solid rgba(249,115,22,0.25)',
            fontSize: '0.84rem',
            color: '#94a3b8',
            lineHeight: 1.6,
          }}>
            📋 Ya tienes una cuenta con <strong style={{ color: '#e2e8f0' }}>{invitation?.email}</strong>.
            Ingresa tu contraseña para aceptar la invitación.
          </div>

          <form className="login-form" onSubmit={handleExistingUser}>
            <div className="field-group">
              <label>Correo electrónico</label>
              <div className="input-wrapper">
                <EmailIcon />
                <input
                  type="email"
                  value={invitation?.email ?? ''}
                  readOnly
                  style={{ opacity: 0.55, cursor: 'not-allowed' }}
                />
              </div>
            </div>

            <div className="field-group">
              <label htmlFor="join-pass-existing">
                Contraseña <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <div className="input-wrapper">
                <LockIcon />
                <input
                  id="join-pass-existing"
                  type={showPwd ? 'text' : 'password'}
                  placeholder="Tu contraseña de StorSafe"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoFocus
                  disabled={submitting}
                />
                <button
                  type="button"
                  className="pwd-toggle"
                  onClick={() => setShowPwd(!showPwd)}
                  tabIndex={-1}
                >
                  {showPwd ? <EyeOffIcon /> : <EyeIcon />}
                </button>
              </div>
            </div>

            {submitError && <p className="login-error">{submitError}</p>}

            <button
              type="submit"
              className="btn-login"
              disabled={submitting || password.length < 8}
            >
              {submitting ? <span className="btn-spinner" /> : 'Aceptar invitación'}
            </button>
          </form>

          <p style={{ textAlign: 'center', marginTop: 16, fontSize: '0.82rem', color: '#64748b' }}>
            ¿Olvidaste tu contraseña?{' '}
            <a href="/" style={{ color: '#22c55e', textDecoration: 'none' }}>
              Recupérala aquí
            </a>
          </p>

          <p className="login-footer">© 2026 StorSafe · Todos los derechos reservados</p>
        </div>
      </div>
    );
  }

  // ── Form — usuario nuevo ───────────────────────────────────────────────────
  const canSubmit = fullName.trim().length > 0 && password.length >= 8 && password === confirmPwd;

  return (
    <div className="login-root">
      <div className="login-card">
        <div className="login-logo">
          <div className="logo-icon"><StorSafeLogo /></div>
          <h1 className="logo-name">StorSafe</h1>
        </div>

        <p className="login-tagline" style={{ textTransform: 'none', letterSpacing: 0 }}>
          Has sido invitado a unirte a{' '}
          <strong style={{ color: '#e2e8f0' }}>{invitation?.company_name}</strong>
        </p>

        <form className="login-form" onSubmit={handleNewUser}>
          <div className="field-group">
            <label>Correo electrónico</label>
            <div className="input-wrapper">
              <EmailIcon />
              <input
                type="email"
                value={invitation?.email ?? ''}
                readOnly
                style={{ opacity: 0.55, cursor: 'not-allowed' }}
              />
            </div>
          </div>

          <div className="field-group">
            <label htmlFor="join-name">
              Nombre completo <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <div className="input-wrapper">
              <UserIcon />
              <input
                id="join-name"
                type="text"
                placeholder="Juan Pérez"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
                autoFocus
                disabled={submitting}
              />
            </div>
          </div>

          <div className="field-group">
            <label htmlFor="join-pass">
              Contraseña <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <div className="input-wrapper">
              <LockIcon />
              <input
                id="join-pass"
                type={showPwd ? 'text' : 'password'}
                placeholder="Mínimo 8 caracteres"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                minLength={8}
                required
                disabled={submitting}
              />
              <button
                type="button"
                className="pwd-toggle"
                onClick={() => setShowPwd(!showPwd)}
                tabIndex={-1}
              >
                {showPwd ? <EyeOffIcon /> : <EyeIcon />}
              </button>
            </div>
          </div>

          <div className="field-group">
            <label htmlFor="join-confirm">
              Confirmar contraseña <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <div className="input-wrapper">
              <LockIcon />
              <input
                id="join-confirm"
                type={showPwd ? 'text' : 'password'}
                placeholder="Repite tu contraseña"
                value={confirmPwd}
                onChange={(e) => setConfirmPwd(e.target.value)}
                required
                disabled={submitting}
              />
              <button
                type="button"
                className="pwd-toggle"
                onClick={() => setShowPwd(!showPwd)}
                tabIndex={-1}
              >
                {showPwd ? <EyeOffIcon /> : <EyeIcon />}
              </button>
            </div>
            {confirmPwd && password !== confirmPwd && (
              <p style={{ color: '#f87171', fontSize: '0.78rem', marginTop: 2 }}>
                Las contraseñas no coinciden
              </p>
            )}
          </div>

          {submitError && <p className="login-error">{submitError}</p>}

          <button
            type="submit"
            className="btn-login"
            disabled={submitting || !canSubmit}
          >
            {submitting ? <span className="btn-spinner" /> : 'Crear mi cuenta'}
          </button>
        </form>

        <p className="login-footer">© 2026 StorSafe · Todos los derechos reservados</p>
      </div>
    </div>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function translateError(msg: string): string {
  if (msg.includes('already registered') || msg.includes('User already registered'))
    return 'Este correo ya tiene una cuenta. Intenta iniciar sesión.';
  if (msg.includes('Password should be'))
    return 'La contraseña debe tener al menos 8 caracteres.';
  if (msg.includes('Network'))
    return 'Sin conexión. Verifica tu internet.';
  return 'Error al crear la cuenta. Intenta nuevamente.';
}

// ── SVG Icons ──────────────────────────────────────────────────────────────────

function StorSafeLogo() {
  return (
    <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="48" height="48" rx="12" fill="#22c55e" />
      <path d="M14 18h20v14a2 2 0 01-2 2H16a2 2 0 01-2-2V18z" fill="white" opacity="0.9" />
      <path d="M12 16a2 2 0 012-2h20a2 2 0 012 2v2H12v-2z" fill="white" />
      <circle cx="24" cy="27" r="3" fill="#22c55e" />
      <rect x="23" y="27" width="2" height="4" rx="1" fill="#22c55e" />
    </svg>
  );
}

function UserIcon() {
  return (
    <svg className="input-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" /><circle cx="12" cy="7" r="4" />
    </svg>
  );
}

function EmailIcon() {
  return (
    <svg className="input-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
      <polyline points="22,6 12,13 2,6" />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg className="input-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0110 0v4" />
    </svg>
  );
}

function EyeIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
}
